import { useState, useRef, useEffect } from "react";
import { ScrollView, View, findNodeHandle, Share, Platform } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { Feather } from "@expo/vector-icons";
import { HeaderButtonProps } from "@react-navigation/elements";
import { openBrowserAsync } from "expo-web-browser";
import { ScreenProps } from "../types";
import { searchClient } from "../../utils/algolia";
import { Button, ScreenContainer, ActionsContainer } from "../../components";
import MainDetailSection from "./MainDetailSection";
import { contentSections } from "./constants";
import { Job, ApplyVia } from "./types";
import NoMatchingJobs from "../JobSearchResultsScreen/NoMatchingJobs";
import { jobUrl } from "../NavigatorScreen/deep-links";

function ShareButton({ job: { jobId, jobTitle: title } }: HeaderButtonProps & { job: Job }) {
  function onPress() {
    const url = jobUrl(jobId);
    const [content, options] =
      Platform.OS === "ios" ? [{ url }, { subject: title }] : [{ message: url, title }, { dialogTitle: title }];

    Share.share(content, options);
  }

  return <Feather testID="share-job" onPress={onPress} size={24} name="share" />;
}

export default function JobDetailScreen({ route, navigation }: ScreenProps<"JobDetail">) {
  const paySectionRef = useRef<View>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const [job, setJob] = useState<Job>();
  const jobId = route.params.id;
  const [loading, setLoading] = useState(false);

  async function getJob() {
    setLoading(true);
    const {
      hits: [job],
    } = await searchClient.search<Job>("", {
      filters: `jobId:${jobId}`,
    });
    if (job) {
      setJob(job);
      navigation.setOptions({ headerRight: () => <ShareButton job={job} /> });
    }
    setLoading(false);
  }

  const handleApply = () => {
    if (job && job.apply_via === ApplyVia.FacilityWebsite) openBrowserAsync(job.external_job_url);
    else
      job &&
        navigation.navigate("JobApply", {
          id: job.jobId,
          companyName: job.facilities[0].facility_name,
        });
  };

  const scrollToPaySection = () => {
    if (paySectionRef.current && scrollViewRef.current) {
      const scrollViewNodeHandle = findNodeHandle(scrollViewRef.current);
      if (scrollViewNodeHandle) {
        paySectionRef.current.measureLayout(scrollViewNodeHandle, (x: number, y: number) => {
          scrollViewRef.current?.scrollTo({ x, y, animated: true });
        });
      }
    }
  };

  useEffect(() => {
    getJob();
  }, [jobId]);

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center pb-20 bg-white">
        <ActivityIndicator testID="job-detail-screen-activity-indicator" />
      </View>
    );
  }

  if (!job) {
    return (
      <ScreenContainer>
        <NoMatchingJobs navigation={navigation} />
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScrollView className="max-w-3xl mx-auto w-full" ref={scrollViewRef}>
        <View className="py-4">
          <MainDetailSection job={job} scrollToPaySection={scrollToPaySection} />
          {contentSections
            .filter(({ field }) => job[field])
            .map(({ title, field, Component }) => (
              <Component key={field} title={title} job={job} content={job[field]} paySectionRef={paySectionRef} />
            ))}
        </View>
      </ScrollView>
      <ActionsContainer>
        <Button mode="contained" onPress={handleApply}>
          Apply
        </Button>
      </ActionsContainer>
    </ScreenContainer>
  );
}
